import { useEffect, useRef } from 'react';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import PublicReveal, { publicRevealStagger } from './PublicReveal';
import PublicStoreBadges from './PublicStoreBadges';
import '../../styles/publicVacationsShowcase.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';

const VACATIONS_HERO_IMAGE = `${PUBLIC_URL}/images/hero-banner-4.jpg`;
const VACATIONS_COLLAGE_IMAGE = `${PUBLIC_URL}/images/pexels-mikhail-nilov-9159042.jpg`;

const MOBILE_APP_FEATURES = [
  {
    icon: 'schedule' as const,
    label: 'Suivi planning',
    iconClass: 'public-vacations-feature-icon--violet',
  },
  {
    icon: 'notifications_active' as const,
    label: 'Alertes et rappels',
    iconClass: 'public-vacations-feature-icon--emerald',
  },
  {
    icon: 'school' as const,
    label: 'Documentations',
    iconClass: 'public-vacations-feature-icon--amber',
  },
  {
    icon: 'payments' as const,
    label: 'Mobile Money ou portefeuille',
    iconClass: 'public-vacations-feature-icon--sky',
  },
];

const STAT_AVATARS = [
  `${PUBLIC_URL}/images/hero-banner-1.jpg`,
  `${PUBLIC_URL}/images/hero-banner-2.jpg`,
  `${PUBLIC_URL}/images/hero-banner-3.jpg`,
];

function useBentoCardTilt(sectionRef: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = sectionRef.current;
    if (!root) return undefined;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return undefined;

    const onMove = (event: MouseEvent) => {
      const cards = root.querySelectorAll<HTMLElement>('.public-vacations-bento-card');
      const mouseX = event.clientX / window.innerWidth - 0.5;
      const mouseY = event.clientY / window.innerHeight - 0.5;

      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const inside =
          event.clientX > rect.left &&
          event.clientX < rect.right &&
          event.clientY > rect.top &&
          event.clientY < rect.bottom;

        if (inside) {
          card.style.transform = `perspective(1000px) rotateY(${mouseX * 10}deg) rotateX(${-mouseY * 10}deg) translateY(-5px)`;
          card.style.transition = 'transform 0.1s ease-out';
        } else {
          card.style.transform = 'translateY(0)';
          card.style.transition = 'transform 0.5s ease-in-out';
        }
      });
    };

    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [sectionRef]);
}

export default function PublicVacationsShowcase() {
  const sectionRef = useRef<HTMLElement>(null);
  useBentoCardTilt(sectionRef);

  return (
    <section
      id="vacations"
      ref={sectionRef}
      className="public-vacations-showcase public-section-after-wave scroll-mt-20 overflow-x-clip"
      aria-label="Application mobile et chiffres clés"
    >
      <div className="relative z-[1] mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
        <div className="public-vacations-hero-shell overflow-hidden rounded-public-card p-8 sm:p-10 lg:p-12">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
            <PublicReveal animation="fade-in-left" className="flex flex-col gap-8 lg:col-span-5">
              <div className="flex flex-col gap-4">
                <p className="text-xs font-bold uppercase tracking-widest text-primary-fixed-dim">Application mobile</p>
                <h2 className="text-3xl font-bold leading-[1.1] tracking-tight text-white sm:text-4xl lg:text-[2.75rem]">
                  Votre parcours, partout avec vous
                </h2>
                <p className="max-w-lg text-base leading-relaxed text-white/60 sm:text-lg">
                  L'application mobile JCONSULT MY Academia sur Android et iOS.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                {MOBILE_APP_FEATURES.map((feature, index) => (
                  <PublicReveal
                    key={feature.icon}
                    animation="fade-in-up"
                    delay={publicRevealStagger(index, 60, 80)}
                    className="flex items-center gap-3"
                  >
                    <div
                      className={`public-vacations-feature-icon flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${feature.iconClass}`}
                    >
                      <LucideSymbolIcon name={feature.icon} tone="inherit" size={18} />
                    </div>
                    <span className="text-sm font-semibold text-white">{feature.label}</span>
                  </PublicReveal>
                ))}
              </div>

              <div className="flex flex-col gap-3">
                <PublicStoreBadges theme="dark" />
                <p className="text-xs text-white/45">
                  Gratuite — connectez-vous avec le même compte que sur le site web.
                </p>
              </div>
            </PublicReveal>

            <PublicReveal animation="fade-in-right" className="lg:col-span-7">
              <div className="public-vacations-collage" aria-hidden={false}>
                <div className="public-vacations-collage__abstract">
                  <div className="public-vacations-collage__abstract-glow public-vacations-collage__abstract-glow--1" />
                  <div className="public-vacations-collage__abstract-glow public-vacations-collage__abstract-glow--2" />
                  <div className="public-vacations-collage__abstract-shapes">
                    <div className="public-vacations-collage__oval public-vacations-collage__oval--outline" />
                    <div className="public-vacations-collage__oval public-vacations-collage__oval--fill" />
                  </div>
                </div>

                <div className="public-vacations-collage__portrait">
                  <img
                    src={VACATIONS_HERO_IMAGE}
                    alt="Apprenant en formation au campus JCONSULT MY Academia"
                    className="h-full w-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="public-vacations-collage__portrait-overlay" />
                </div>

                <div className="public-vacations-collage__badge">
                  <div className="public-vacations-collage__badge-inner">
                    <LucideSymbolIcon name="auto_awesome" tone="inherit" className="text-blue-300" size={48} filled />
                  </div>
                </div>

                <div className="public-vacations-collage__asset">
                  <div className="public-vacations-collage__asset-glow" />
                  <div className="public-vacations-collage__asset-image-wrap">
                    <img
                      src={VACATIONS_COLLAGE_IMAGE}
                      alt="Formation professionnelle en présentiel"
                      className="public-vacations-collage__asset-image"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                </div>
              </div>
            </PublicReveal>
          </div>
        </div>

        <div className="public-vacations-bento-stats mt-10 grid grid-cols-1 items-stretch gap-6 md:grid-cols-4 lg:mt-12">
          <PublicReveal animation="fade-in-up" className="public-vacations-bento-stats__item md:col-span-2">
            <article className="public-vacations-bento-card flex h-full flex-col justify-between rounded-public-card p-8">
              <div>
                <h3 className="mb-4 text-2xl font-bold text-white sm:text-3xl">19+ Formations professionnelles</h3>
                <p className="text-sm leading-relaxed text-white/60 sm:text-base">
                  Des parcours complets pour maîtriser les compétences recherchées sur le marché de l&apos;emploi à
                  Kinshasa et en RDC.
                </p>
              </div>
              <div className="mt-6 flex -space-x-4">
                {STAT_AVATARS.map((src, index) => (
                  <div
                    key={src}
                    className="h-12 w-12 overflow-hidden rounded-full border-2 border-slate-950 bg-slate-800"
                  >
                    <img
                      src={src}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                      decoding="async"
                      aria-hidden={index > 0}
                    />
                  </div>
                ))}
                <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-slate-950 bg-violet-500/20 text-sm font-bold text-violet-300">
                  19+
                </div>
              </div>
            </article>
          </PublicReveal>

          <PublicReveal animation="fade-in-up" delay={100} className="public-vacations-bento-stats__item">
            <article className="public-vacations-bento-card flex h-full flex-col gap-6 rounded-public-card p-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-violet-500/15 text-violet-300">
                <LucideSymbolIcon name="widgets" tone="inherit" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">5 Domaines de formation</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">
                  Une organisation structurée par domaines pour une progression cohérente.
                </p>
              </div>
            </article>
          </PublicReveal>

          <PublicReveal animation="fade-in-up" delay={180} className="public-vacations-bento-stats__item">
            <article className="public-vacations-bento-card flex h-full flex-col gap-6 rounded-public-card p-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-sky-500/15 text-sky-300">
                <LucideSymbolIcon name="rocket_launch" tone="inherit" size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">6 Vacations par jour</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/60">
                  Un rythme d&apos;apprentissage intensif et flexible adapté aux professionnels en activité.
                </p>
              </div>
            </article>
          </PublicReveal>
        </div>
      </div>
    </section>
  );
}
